/**
 * batch-53 acquisitie-vervolg (FO-407) - pure datum-helpers voor het kopje
 * "Vervolgcontacten" en het prominentere vervolgdatum-veld.
 *
 * Waarom hier en niet in de route/het component: de urgentie-band (verlopen /
 * vandaag / later) en de snelkeuze-chips (morgen / volgende week / over 2 weken
 * / over een maand) moeten aan BEIDE kanten identiek uitpakken - de server
 * kleurt en telt, de client toont dezelfde datum als hij op een chip klikt.
 * Zo blijft er één waarheid, testbaar zonder DB of render.
 *
 * Alles rekent in `Europe/Amsterdam` (CLAUDE.md): de DB slaat UTC op, maar een
 * vervolgcontact "van vandaag" is een NL-kalenderdag. Zonder die conversie zou
 * een alarm van 00:30 NL in de winter als "gisteren" gelden.
 */

import { amsterdamAllDayStartToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'

/** Urgentie-band van een vervolgdatum t.o.v. vandaag (NL-tijd). */
export type VervolgBandWaarde = 'VERLOPEN' | 'VANDAAG' | 'LATER'

/** NL-kalenderdag (`yyyy-mm-dd`) van een UTC-instant. */
export function nlDatumSleutel(d: Date): string {
  return utcToAmsterdamWallclock(d).ymd
}

/** `yyyy-mm-dd` + n dagen, kalender-correct (schrikkeljaar/maandgrens). */
export function verschuifDatumSleutel(ymd: string, dagen: number): string {
  const [jaar, maand, dag] = ymd.split('-').map(Number)
  if (!jaar || !maand || !dag) return ymd
  // UTC-rekenen op een kale kalenderdatum: geen DST-sprong mogelijk omdat er
  // geen tijdcomponent aan hangt (we converteren pas op de rand naar een
  // UTC-instant via `amsterdamAllDayStartToUtc`).
  const basis = new Date(Date.UTC(jaar, maand - 1, dag))
  basis.setUTCDate(basis.getUTCDate() + dagen)
  return basis.toISOString().slice(0, 10)
}

/**
 * `yyyy-mm-dd` + n maanden. Klemt op de laatste dag van de doelmaand zodat
 * 31-01 + 1 maand op 28/29-02 uitkomt i.p.v. door te rollen naar maart (de
 * `setUTCMonth`-val uit het projectgeheugen).
 */
export function verschuifMaandSleutel(ymd: string, maanden: number): string {
  const [jaar, maand, dag] = ymd.split('-').map(Number)
  if (!jaar || !maand || !dag) return ymd
  const doelMaandIndex = maand - 1 + maanden
  const doelJaar = jaar + Math.floor(doelMaandIndex / 12)
  const doelMaand = ((doelMaandIndex % 12) + 12) % 12
  // Dag 0 van de VOLGENDE maand = laatste dag van de doelmaand.
  const laatsteDag = new Date(Date.UTC(doelJaar, doelMaand + 1, 0)).getUTCDate()
  const doelDag = Math.min(dag, laatsteDag)
  return new Date(Date.UTC(doelJaar, doelMaand, doelDag)).toISOString().slice(0, 10)
}

/** De vier snelkeuzes uit FO §A.1, in de volgorde waarin ze in de UI staan. */
export const VERVOLG_SNELKEUZES = [
  { key: 'morgen', label: 'Morgen', dagen: 1 },
  { key: 'volgende-week', label: 'Volgende week', dagen: 7 },
  { key: 'twee-weken', label: 'Over 2 weken', dagen: 14 },
  { key: 'maand', label: 'Over een maand', maanden: 1 },
] as const

export type VervolgSnelkeuzeKey = (typeof VERVOLG_SNELKEUZES)[number]['key']

/**
 * De datum (`yyyy-mm-dd`) achter een snelkeuze-chip, gerekend vanaf de NL-dag
 * van `nu`. Onbekende key -> vandaag (kan niet voorkomen via de tuple, maar de
 * functie blijft zo totaal).
 */
export function snelkeuzeDatum(key: VervolgSnelkeuzeKey, nu: Date = new Date()): string {
  const vandaag = nlDatumSleutel(nu)
  const keuze = VERVOLG_SNELKEUZES.find((k) => k.key === key)
  if (!keuze) return vandaag
  if ('maanden' in keuze) return verschuifMaandSleutel(vandaag, keuze.maanden)
  return verschuifDatumSleutel(vandaag, keuze.dagen)
}

/**
 * Band van een vervolgdatum: verlopen (< vandaag), vandaag, of later.
 * Vergelijkt op NL-kalenderdag, niet op het instant - een alarm dat als
 * 12:00 UTC is opgeslagen valt zo altijd op de bedoelde dag.
 */
export function vervolgBand(alarmDatum: Date, nu: Date = new Date()): VervolgBandWaarde {
  const dag = nlDatumSleutel(alarmDatum)
  const vandaag = nlDatumSleutel(nu)
  if (dag < vandaag) return 'VERLOPEN'
  if (dag === vandaag) return 'VANDAAG'
  return 'LATER'
}

/**
 * De UTC-grenzen van "vandaag" in NL-tijd. `[vandaagStart, morgenStart)` is de
 * vandaag-range; alles `< vandaagStart` is verlopen. Beide kanten (query-where,
 * cron-telling) gebruiken deze ene bron.
 */
export function nlDagGrenzen(nu: Date = new Date()): {
  vandaagYmd: string
  vandaagStart: Date
  morgenStart: Date
} {
  const vandaagYmd = nlDatumSleutel(nu)
  return {
    vandaagYmd,
    vandaagStart: amsterdamAllDayStartToUtc(vandaagYmd),
    morgenStart: amsterdamAllDayStartToUtc(verschuifDatumSleutel(vandaagYmd, 1)),
  }
}

/**
 * De UTC-instant waarop een gekozen kalenderdag als vervolgmoment wordt
 * opgeslagen. Bewust 12:00 NL i.p.v. 00:00: de bestaande notitie-flow schrijft
 * al een middag-instant weg, en een middagpunt kan door geen enkele
 * tijdzone-afronding op de vorige/volgende dag belanden.
 */
export function vervolgDatumNaarUtc(ymd: string): Date {
  const start = amsterdamAllDayStartToUtc(ymd)
  return new Date(start.getTime() + 12 * 60 * 60 * 1000)
}
